/**
 * Firebase Configuration
 *
 * Initializes Firebase app with environment-specific settings.
 * Uses emulator in local development.
 */

import { type FirebaseApp, initializeApp } from 'firebase/app';
import { type Auth, connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';

import { logger } from '../utils/logger';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env['VITE_FIREBASE_API_KEY'],
  authDomain: import.meta.env['VITE_FIREBASE_AUTH_DOMAIN'],
  projectId: import.meta.env['VITE_FIREBASE_PROJECT_ID'],
  storageBucket: import.meta.env['VITE_FIREBASE_STORAGE_BUCKET'],
  messagingSenderId: import.meta.env['VITE_FIREBASE_MESSAGING_SENDER_ID'],
  appId: import.meta.env['VITE_FIREBASE_APP_ID'],
};

// Initialize Firebase
let app: FirebaseApp;
let auth: Auth;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);

  // Connect to emulator in local development
  const useEmulator = import.meta.env['VITE_FIREBASE_USE_EMULATOR'] === 'true';
  const emulatorHost = import.meta.env['VITE_FIREBASE_EMULATOR_HOST'] || 'localhost:9199';

  if (useEmulator && import.meta.env.DEV) {
    logger.info({ component: 'firebase' }, `Firebase Auth using emulator at ${emulatorHost}`);
    const [host, port] = emulatorHost.split(':');
    connectAuthEmulator(auth, `http://${host}:${port}`, {
      disableWarnings: true,
    });

    const fnEmulatorHost =
      import.meta.env['VITE_FIREBASE_FUNCTIONS_EMULATOR_HOST'] || 'localhost:5001';
    const [fnHost, fnPort] = fnEmulatorHost.split(':');
    connectFunctionsEmulator(getFunctions(app, 'us-central1'), fnHost, parseInt(fnPort, 10));
    logger.info(
      { component: 'firebase' },
      `Firebase Functions using emulator at ${fnEmulatorHost}`,
    );
  } else {
    logger.info({ component: 'firebase' }, 'Firebase Auth using production');
  }
} catch (error) {
  logger.error(
    { component: 'firebase' },
    'Failed to initialize Firebase',
    error instanceof Error ? error : new Error(String(error)),
  );
  throw error;
}

export { app, auth };
export default app;

export function getSendChangeEmailVerification(): ReturnType<typeof httpsCallable> {
  return httpsCallable(getFunctions(app, 'us-central1'), 'sendChangeEmailVerification');
}

export function getSendPasswordResetEmail(): ReturnType<typeof httpsCallable> {
  return httpsCallable(getFunctions(app, 'us-central1'), 'sendPasswordResetEmail');
}

export function getSendVerificationEmail(): ReturnType<typeof httpsCallable> {
  return httpsCallable(getFunctions(app, 'us-central1'), 'sendVerificationEmail');
}
